/*
 * component-sticky-cart.js — sticky Add To Cart fix (deployed 24 Oct 2026; previous version in the editor's file history)
 * Carbonetics theme, sticky "Add To Cart" bar (shows on screens ≤ 640px wide)
 *
 * What changed vs the live file (assets/component-sticky-cart.js):
 *
 *  FIX 1  Material-picker pages (product.r32-material / r32-material-freeship templates)
 *         now ADD TO CART from the sticky bar. The tap presses the main Add to Cart button,
 *         so it adds exactly what the customer has selected (material + matte), same as
 *         the main button. Previously the tap tried to scroll to the theme's stock
 *         <variant-selects> dropdown, which is hidden on these templates, so nothing happened.
 *
 *  FIX 2  On every other page that stays in "scroll to options" mode, the scroll target
 *         must actually be visible (custom #material-selector first, then the stock
 *         pickers, then the product form). No more dead taps.
 *
 *  FIX 3  The bar no longer pops up on top of the page while the main Add to Cart is
 *         already on screen (the 4-second start-up timer used to force it open).
 *
 *  FIX 4  The "scroll to options" click listener is only attached once (it was attached twice).
 *
 * Everything else (preorder label, icon, 1-second double-tap guard) is unchanged.
 */
if (!customElements.get("sticky-cart")) {
  class StickyCart extends HTMLElement {
    constructor() {
      super();
      this.container = this;
      this.buttonTrigger = this.container.querySelector(".sticky-cart__action button");
      this.svgCartIcon = `<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-cart" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M4.5 8.01995L8.52569 3.62829C9.31836 2.76355 10.6816 2.76355 11.4743 3.62829L15.5 8.01995M17.5975 9.31685L15.4243 16.3168C15.2944 16.7352 14.9073 17.0204 14.4693 17.0204H5.58499C5.14911 17.0204 4.76345 16.738 4.63173 16.3225L2.41278 9.32253C2.2084 8.67779 2.68968 8.02035 3.36603 8.02035H16.6425C17.3163 8.02035 17.7973 8.67329 17.5975 9.31685Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
      </svg>`;
      this.formInView = false; // FIX 3: tracked by the IntersectionObserver
      this.linkBound = false; // FIX 4
      this.addBound = false;
      this.waitForButtonState();
    }

    connectedCallback() {
      this.observeMainForm();
      // FIX 4: the live file also called handleOptionsLink() here, then again in setButtonLogic().
    }

    disconnectedCallback() {
      this.observer && this.observer.disconnect();
    }

    observeMainForm() {
      this.observer && this.observer.disconnect();
      this.targetEl = document.querySelector("form[data-type=add-to-cart-form]");
      if (!this.targetEl) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          this.formInView = entry.isIntersecting; // FIX 3
          if (entry.isIntersecting) {
            this.container.classList.remove("sticky-cart--open");
          } else if (this.buttonTrigger.children.length > 0) {
            this.container.classList.add("sticky-cart--open");
          }
        },
        { threshold: [0, 0.1], rootMargin: "0px 0px 0px 0px" }
      );
      this.observer.observe(this.targetEl);
    }

    // FIX 2: only scroll to something the customer can actually see
    getScrollTarget() {
      const candidates = [
        "#material-selector",
        ".product__info-container variant-selects",
        ".product__info-container variant-radios",
        ".product__info-container product-form",
      ];
      for (const selector of candidates) {
        const el = document.querySelector(selector);
        if (el && el.getClientRects().length > 0) return el;
      }
      return null;
    }

    handleOptionsLink() {
      if (this.linkBound) return; // FIX 4
      this.linkBound = true;
      this.buttonTrigger.addEventListener("click", () => {
        const target = this.getScrollTarget();
        target && target.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }

    async waitForButtonState() {
      const preorderPromise = this.waitForElement(".essential-preorder-container-active", 4000);
      const avpPromise = this.waitForElement("#avpoptions-container__v2", 4000);
      try {
        const isOnlyDefaultVariant = this.buttonTrigger?.getAttribute("data-only-default-variant") === "true";
        // FIX 1: pages with the custom material picker add straight to cart
        const hasMaterialPicker = !!document.querySelector("#material-selector");
        const [preorderEl, avpEl] = await Promise.all([preorderPromise, avpPromise]);
        let result;
        if (hasMaterialPicker) {
          result = { type: "action", label: "Add To Cart", showCartIcon: true };
        } else if (preorderEl) {
          result = {
            type: "link",
            label:
              preorderEl.querySelector(".essential-preorder-initial-add-to-cart-text span")?.textContent ||
              "Order Now (Made To Order)",
            showCartIcon: false,
          };
        } else if (avpEl) {
          result = { type: "link", label: "Add To Cart", showCartIcon: true };
        } else {
          result = { type: isOnlyDefaultVariant ? "action" : "link", label: "Add To Cart", showCartIcon: true };
        }
        this.setButtonLogic(result.type, result.label, result.showCartIcon);
      } catch (err) {
        console.error("Button state error:", err);
        this.setButtonLogic("link", "Add To Cart", true);
      }
    }

    waitForElement(selector, timeout = 4000) {
      return new Promise((resolve) => {
        const element = document.querySelector(selector);
        if (element) {
          resolve(element);
          return;
        }
        const observer = new MutationObserver(() => {
          const el = document.querySelector(selector);
          if (el) {
            observer.disconnect();
            resolve(el);
          }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => {
          observer.disconnect();
          resolve(null);
        }, timeout);
      });
    }

    setButtonLogic(trigger, label, showCartIcon = false) {
      this.buttonTrigger.insertAdjacentHTML(
        "beforeend",
        `<span>
            ${label}
            ${showCartIcon ? this.svgCartIcon : ""}
          </span>`
      );
      if (trigger == "link") {
        this.buttonTrigger.classList.add("sticky-cart__action--link");
        this.handleOptionsLink();
      } else {
        this.buttonTrigger.classList.remove("sticky-cart__action--link");
        this.buttonTrigger.classList.add("sticky-cart__action--add");
        this.handleAddToCart();
      }
      // FIX 3: was `!this.isVisible && ...` — isVisible was never updated, so the bar was
      // forced open even when the main Add to Cart button was already on screen.
      if (!this.formInView && !this.container.classList.contains("sticky-cart--open")) {
        this.container.classList.add("sticky-cart--open");
      }
    }

    handleAddToCart() {
      if (this.addBound) return;
      this.addBound = true;
      this.buttonTrigger.addEventListener("click", () => {
        if (this.buttonTrigger.getAttribute("aria-disabled") === "true") return;
        const mainButton = document.querySelector(".product__info-container .product-form__submit[type=submit]");
        if (!mainButton) return;
        this.buttonTrigger.setAttribute("aria-disabled", true);
        mainButton.click();
        setTimeout(() => {
          this.buttonTrigger.setAttribute("aria-disabled", false);
        }, 1000);
      });
    }
  }
  customElements.define("sticky-cart", StickyCart);
}
